import { ChangeDetectionStrategy, Component, model, computed, inject, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { dia } from '@clientio/rappid';
import { TranslocoPipe } from '@jsverse/transloco';
import { FlowchartComponent } from 'shared/feature-flowchart';
import {
  ChipComponent,
  ConfirmationDialogComponent,
  DialogService,
  IconDirective,
  MenuComponent,
  MenuItemDirective,
  MenuTriggerDirective,
  SpinnerComponent,
  TooltipDirective,
  ButtonDirective,
} from 'shared/ui-common';
import { isTruthy } from 'shared/util-common';

import { FlowchartNodeDto, FlowchartNodeType } from '@treetop/shared/model';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-compare-flowchart',
  templateUrl: './compare-flowchart.component.html',
  styleUrls: ['./compare-flowchart.component.scss'],
  imports: [
    MatIconModule,
    FlowchartComponent,
    TooltipDirective,
    IconDirective,
    TranslocoPipe,
    SpinnerComponent,

    ChipComponent,
    MenuComponent,
    MenuItemDirective,
    MenuTriggerDirective,
    ButtonDirective,
  ],
})
export class CompareFlowchartComponent {
  private dialogService = inject(DialogService);

  public flowchartId = input.required<string>();
  public compareMode = model(false);
  public mergeConflictStatus = input<'conflict' | 'resolved' | undefined>();
  public original = input.required<FlowchartNodeType[]>();
  public local = input<FlowchartNodeType[]>([]);

  public allowFullScreen = input(true);
  protected editMode = input(false);

  public localChanged = output<FlowchartNodeDto[]>();
  public conflictResolved = output();
  public conflictUnresolved = output();

  public exportFlowchartClicked = output<dia.Paper>();
  public exportPdfFlowchartClicked = output<dia.Paper>();

  protected renderFlowchart = signal(true);

  protected hasLocalChanges = computed(() => {
    return !!this.local();
  });

  protected showHeader = computed(() => this.mergeConflictStatus() === 'resolved' || this.compareMode());

  protected handleLocalChanged(flowchartElements: FlowchartNodeDto[]): void {
    this.localChanged.emit(flowchartElements);
    this.conflictResolved.emit();
  }

  protected revertLocalFlowchart() {
    this.dialogService
      .open(ConfirmationDialogComponent, {
        title: 'SingleGuidelineManager.ImportedGuideline.Editor.RevertToOriginalConfirmDialog.Title',
        data: {
          message: 'SingleGuidelineManager.ImportedGuideline.Editor.RevertToOriginalConfirmDialog.Message',
          cancelButtonCaption: 'Cancel',
          confirmButtonCaption: 'SingleGuidelineManager.ImportedGuideline.Editor.RevertToOriginalConfirmDialog.Confirm',
        },
      })
      .afterClosed()
      .pipe(isTruthy())
      .subscribe(() => {
        this.localChanged.emit(undefined);

        if (this.mergeConflictStatus() === 'conflict') {
          this.conflictResolved.emit();
        }

        this.renderFlowchart.set(false);

        setTimeout(() => this.renderFlowchart.set(true), 300);
      });
  }

  protected handleExportFlowchart($event: dia.Paper) {
    this.exportFlowchartClicked.emit($event);
  }

  protected handleExportPdfFlowchart($event: dia.Paper) {
    this.exportPdfFlowchartClicked.emit($event);
  }

  protected handleAcceptLocalClicked() {
    if (this.mergeConflictStatus() === 'conflict') {
      this.conflictResolved.emit();
    }
  }

  protected handleAcceptOriginalClicked() {
    this.revertLocalFlowchart();
  }
}
